/**
 * Delegações: consultas e a troca de contexto ("atuar na conta de…").
 * docs/PLANO_DELEGACAO.md — Bloco 4.
 */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { eDeColega } from '@/api/client';
import { registrarVisualizacao } from '@/api/compartilhamento';
import {
  fetchDelegacoesDadas,
  fetchDelegacoesRecebidas,
  fetchMembrosElegiveis,
  fetchResumoRecebidas,
  type Delegacao,
} from '@/api/delegacoes';
import { useProcessoByNumero } from '@/hooks/useProcessoByNumero';
import { PREFIXO_CHAVE_DO_DONO } from '@/lib/access/accessOracle';
import { getAtuacao, PREFIXO_HASH_DELEGADO, setAtuacao, useAtuacao } from '@/lib/delegacao/atuacao';
import { queryKeys } from '@/lib/pwa/queryConfig';
import { getSyncState, processOutbox, subscribeSyncState } from '@/lib/pwa/syncManager';

import { useAuth } from './useAuth';

export function useDelegacoesRecebidas() {
  const { user } = useAuth();
  return useQuery({
    queryKey: [...queryKeys.delegacoes.recebidas, user?.id ?? null],
    enabled: !!user?.id,
    queryFn: fetchDelegacoesRecebidas,
    staleTime: 60 * 1000,
    // O titular pode mudar o nível (ou revogar) com a aba do delegado aberta: voltar à
    // aba revalida — é o que mantém menu e botões de acordo com o que o backend permite.
    refetchOnWindowFocus: true,
  });
}

export function useResumoRecebidas(enabled = true) {
  const { user } = useAuth();
  return useQuery({
    queryKey: [...queryKeys.delegacoes.resumo, user?.id ?? null],
    enabled: enabled && !!user?.id,
    queryFn: fetchResumoRecebidas,
    staleTime: 60 * 1000,
  });
}

/** Só para quem delega (tem OAB): a rota fica atrás do gate de assinatura. */
export function useDelegacoesDadas(enabled = true) {
  const { user } = useAuth();
  return useQuery({
    queryKey: [...queryKeys.delegacoes.dadas, user?.id ?? null],
    enabled: enabled && !!user?.id,
    queryFn: fetchDelegacoesDadas,
  });
}

export function useMembrosElegiveis(enabled: boolean) {
  const { user } = useAuth();
  return useQuery({
    queryKey: [...queryKeys.delegacoes.elegiveis, user?.id ?? null],
    enabled: enabled && !!user?.id,
    queryFn: fetchMembrosElegiveis,
  });
}

/**
 * Entra na conta do titular (ou volta para a própria, com `null`).
 *
 * - Espera o outbox terminar de sincronizar: a reprodução lê o contexto a cada
 *   requisição, e trocar no meio mandaria a ação para a conta errada.
 * - Descarta da memória o cache do contexto que sai (o delegado não o leva
 *   consigo); o da própria conta fica, e o persistido nunca teve dado delegado.
 * - Drena as pendências do contexto em que entra (cada entry só roda no dela).
 */
/** A conta em que se passa a atuar: uma delegação recebida, ou o "visualizar como" (A8). */
export interface ContaParaAtuar {
  id: string;
  titular: { id: string; nome: string };
  nivel: Delegacao['nivel'];
  permite_ia: boolean;
  supervisao?: boolean;
}

/** O "visualizar como" de um membro, na forma de uma conta em que se atua (Leitura). */
export function contaDaSupervisao(s: { usuario: { id: string; nome: string } }): ContaParaAtuar {
  return { id: `supervisao:${s.usuario.id}`, titular: s.usuario, nivel: 'LEITURA', permite_ia: false, supervisao: true };
}

export function useTrocarAtuacao() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuth();

  return useCallback(
    (delegacao: ContaParaAtuar | null, destino = '/app') => {
      if (!user) return;
      const executar = async () => {
        const anterior = getAtuacao();
        // Toda entrada do gestor na conta de um membro fica na auditoria (A8): o servidor só
        // abre o contexto com ela registrada, então se registra ANTES de trocar.
        if (delegacao?.supervisao) {
          try {
            await registrarVisualizacao(delegacao.titular.id);
          } catch {
            toast.error(`Não deu para abrir a conta de ${delegacao.titular.nome}. Tente de novo.`);
            return;
          }
        }
        setAtuacao(
          delegacao
            ? {
                delegacaoId: delegacao.id,
                titularId: delegacao.titular.id,
                titularNome: delegacao.titular.nome,
                nivel: delegacao.nivel,
                permiteIa: delegacao.permite_ia,
                atorId: user.id,
                ...(delegacao.supervisao ? { supervisao: true, registradaEm: Date.now() } : {}),
              }
            : null,
        );
        if (anterior && anterior.titularId !== delegacao?.titular.id) {
          const prefixo = `${PREFIXO_HASH_DELEGADO}${anterior.titularId}:`;
          queryClient.removeQueries({ predicate: (q) => q.queryHash.startsWith(prefixo) });
        }
        void processOutbox();
        // Reforço no momento da troca (o seletor e o filete ficam; o aviso, não).
        if (delegacao && anterior?.delegacaoId !== delegacao.id) {
          toast.info(`Você está na conta de ${delegacao.titular.nome}`);
        }
        navigate(destino);
      };
      if (!getSyncState().isSyncing) {
        void executar();
        return;
      }
      toast.info('Terminando de sincronizar ações pendentes…');
      const cancelar = subscribeSyncState(() => {
        if (getSyncState().isSyncing) return;
        cancelar();
        void executar();
      });
    },
    [navigate, queryClient, user],
  );
}

/**
 * Chave do acesso aos autos (AccessOracle e gates que antes olhavam o CPF do
 * usuário). No contexto delegado os autos são consultados com o certificado do
 * titular — o CPF próprio do delegado (que nem precisa ter) não entra.
 *
 * Com o ``numero``: no processo de um colega (sem a linha própria do CNJ), o backend
 * lê os autos com o certificado do DONO (Bloco 3b do plano do escritório) — a chave é
 * a dele, e quem não tem OAB (o estagiário) também abre. Enquanto se descobre de quem é
 * o processo, vazia (a tela espera). Sem rede, o CPF próprio, como sempre.
 */
export function useChaveDeAcesso(numero?: string): string {
  const { user } = useAuth();
  const atuacao = useAtuacao();
  const doProcesso = useProcessoByNumero(atuacao ? undefined : numero);
  if (atuacao) return `titular:${atuacao.titularId}`;
  const processo = doProcesso.data;
  if (processo && eDeColega(processo) && processo.dono) return `${PREFIXO_CHAVE_DO_DONO}${processo.dono.id}`;
  // Até saber de quem é o processo, vazia: uma negativa vinda com o certificado do dono não
  // pode ficar gravada no CPF próprio. É uma espera curta (consulta local) e só na
  // primeira abertura, enquanto o by-numero não está no cache.
  if (numero && doProcesso.isPending && doProcesso.fetchStatus === 'fetching') return '';
  return user?.cpf ?? '';
}
